import React from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { openCharacter } from "../utils/characterUtils";
import { getUserEmail } from "../utils/userUtils";

const CharacterCard = ({ character, onCharacterOpen }) => {
  const { t } = useTranslation();
  const { user } = useAuthenticator((context) => [context.user]);
  
  const isLoggedIn = !!user;
  const userEmail = getUserEmail(user);

  // Handle character interaction (open chat)
  const handleCharacterClick = async () => {
    if (!isLoggedIn) {
      // Redirect to login - this will be handled by the Link component
      return;
    }

    try {
      // Track that user opened this character
      await openCharacter(userEmail, character.character_id);
      
      // Notify parent component
      if (onCharacterOpen) {
        onCharacterOpen(character);
      }
    } catch (error) {
      console.error("Error opening character:", error);
    }
  };

  // Get character avatar (placeholder for now)
  const getAvatarUrl = (character) => {
    if (character.avatar_url) {
      return character.avatar_url;
    }
    
    // Built-in character avatars
    if (character.character_id === "0") {
      return "/jarvis_ai_sapiens_small.png";
    } else if (character.character_id === "1") {
      return "/sam_ai_sapiens_small.jpg";
    }
    
    // Default placeholder for custom characters
    return "/placeholder.jpg";
  };

  // Format creation date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleDateString();
  };

  // Determine button text and action
  const buttonText = isLoggedIn ? "Chat" : "Login to Chat";
  
  // Route to appropriate chat interface based on character type
  const getChatPath = (characterId) => {
    if (characterId === "0") return "/myai"; // Jarvis route
    if (characterId === "1") return "/myai1"; // Samantha route  
    // For custom characters, we'll use a generic route with bot_id parameter
    return `/myai?bot_id=${characterId}`;
  };
  
  const buttonPath = isLoggedIn 
    ? getChatPath(character.character_id)
    : "/controlpanel";

  return (
    <Link
      to={buttonPath}
      onClick={handleCharacterClick}
      className="block transform transition-all duration-300 hover:scale-102 cursor-pointer group"
    >
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/10 hover:border-purple-400 transition-all duration-300">
        {/* Character Image */}
        <div className="relative h-96 bg-gradient-to-br from-purple-900/20 to-pink-900/20 overflow-hidden">
          <img
            src={getAvatarUrl(character)}
            alt={character.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              e.target.src = "/placeholder.jpg";
            }}
          />
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          
          {/* Top badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {character.is_builtin && (
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-3 py-1 rounded-full text-xs font-medium">
{t('characterBoard.characterCard.official')}
              </div>
            )}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              AI
            </div>
          </div>

          {/* Status emoji badge */}
          <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md rounded-full p-2 shadow-lg border border-white/20">
            <span className="text-xl">{character.status_emoji || "🤖"}</span>
          </div>

          {/* Character info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h3 className="text-2xl font-light text-white mb-1 drop-shadow-lg tracking-wide">
              {character.name}
            </h3>
            
            {/* Age/Creator line */}
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3 font-light">
              {!character.is_builtin && character.creator_name && (
                <span>by {character.creator_name}</span>
              )}
              {character.is_builtin && (
                <span>{t('characterBoard.characterCard.officialCharacter')}</span>
              )}
            </div>

            {/* Short description */}
            <p className="text-white/80 text-sm leading-relaxed line-clamp-3 drop-shadow-md font-light">
              {character.short_description || character.description || "No description available"}
            </p>
          </div>

          {/* Hover effect overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 to-pink-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="bg-white/90 backdrop-blur-md text-gray-900 px-8 py-3 rounded-full font-medium shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 border border-white/20">
              {isLoggedIn ? t('characterBoard.characterCard.startChat') : t('characterBoard.characterCard.loginToChat')}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CharacterCard;